import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useParams } from 'react-router';

import { useAuth } from '@/features/auth/useAuth';
import { errorKeyOf, type ApiErrorKey } from '@/services/api/errors';
import { OrderNotesApi } from '@/services/api/order-notes-api';
import type { OrderNoteItem } from '@/services/api/types';
import { isAbortError } from '@/pages/orders-page-utils';

/**
 * Local-only setting.
 *
 * Mirrors `SettingsStore.AutoMarkOrderNotesAsRead`
 * in the desktop/web Manager.
 */
const AUTO_MARK_NOTES_AS_READ_STORAGE_KEY = 'mdenture.autoMarkOrderNotesAsRead';

function readAutoMarkNotesAsRead(): boolean {
  try {
    return localStorage.getItem(AUTO_MARK_NOTES_AS_READ_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

function writeAutoMarkNotesAsRead(value: boolean): void {
  try {
    localStorage.setItem(AUTO_MARK_NOTES_AS_READ_STORAGE_KEY, value ? 'true' : 'false');
  } catch {
    // Ignore storage failures, for example in private browsing.
    // The setting simply will not persist.
  }
}

/**
 * Returns notes written by the lab that the customer has not read yet.
 *
 * Mirrors the desktop/web Manager filter.
 */
function unreadLabNoteIds(notes: readonly OrderNoteItem[]): string[] {
  return notes.filter((note) => !note.isCustomerNote && !note.readUtc).map((note) => note.id);
}

/**
 * Upserts `updated` notes into `previous` by ID and keeps the list
 * sorted oldest-first.
 */
function mergeNotes(previous: readonly OrderNoteItem[], updated: readonly OrderNoteItem[]): OrderNoteItem[] {
  const byId = new Map(previous.map((note) => [note.id, note]));

  for (const note of updated) {
    byId.set(note.id, note);
  }

  return [...byId.values()].sort((left, right) => Date.parse(left.createdUtc) - Date.parse(right.createdUtc));
}

export function useOrderNotes() {
  const { client } = useAuth();
  const { orderId } = useParams();

  const [messageText, setMessageText] = useState('');
  const [notes, setNotes] = useState<OrderNoteItem[]>([]);
  const [notesLoading, setNotesLoading] = useState(() => Boolean(client));
  const [notesErrorKey, setNotesErrorKey] = useState<ApiErrorKey | null>(null);

  const [sendingNote, setSendingNote] = useState(false);
  const [sendErrorKey, setSendErrorKey] = useState<ApiErrorKey | null>(null);

  const [hasConflict, setHasConflict] = useState(false);

  const [autoMarkAsRead, setAutoMarkAsRead] = useState(readAutoMarkNotesAsRead);

  const [isMessagesCollapsed, setIsMessagesCollapsed] = useState(false);
  const [isMessagesLarge, setIsMessagesLarge] = useState(false);

  const messagesScrollRef = useRef<HTMLDivElement | null>(null);

  const unreadLabIds = useMemo(() => unreadLabNoteIds(notes), [notes]);
  const unreadLabNoteCount = unreadLabIds.length;
  const firstUnreadLabNoteId = unreadLabIds[0] ?? null;

  const scrollMessagesToBottom = useCallback((behavior: ScrollBehavior = 'auto'): void => {
    const element = messagesScrollRef.current;

    if (!element) {
      return;
    }

    if (typeof element.scrollTo === 'function') {
      element.scrollTo({
        top: element.scrollHeight,
        behavior,
      });
    } else {
      element.scrollTop = element.scrollHeight;
    }
  }, []);

  const reloadOrderNotes = useCallback(async (): Promise<void> => {
    if (!client || !orderId) {
      return;
    }

    setNotesLoading(true);
    setNotesErrorKey(null);

    try {
      const api = new OrderNotesApi(client);
      const loadedNotes = await api.getOrderNotes(orderId);

      setNotes(loadedNotes);
    } catch (error) {
      setNotesErrorKey(errorKeyOf(error));
    } finally {
      setNotesLoading(false);
    }
  }, [client, orderId]);

  /**
   * Initial notes load.
   */
  useEffect(() => {
    if (!client || !orderId) {
      return;
    }

    const abort = new AbortController();
    const api = new OrderNotesApi(client);

    void api
      .getOrderNotes(orderId, undefined, abort.signal)
      .then((loadedNotes) => {
        setNotes(loadedNotes);
      })
      .catch((error) => {
        if (isAbortError(error)) {
          return;
        }

        setNotesErrorKey(errorKeyOf(error));
      })
      .finally(() => {
        if (!abort.signal.aborted) {
          setNotesLoading(false);
        }
      });

    return () => {
      abort.abort();
    };
  }, [client, orderId]);

  /**
   * Automatically marks unread lab notes as read when the setting
   * is enabled.
   */
  useEffect(() => {
    if (!client || !orderId || !autoMarkAsRead) {
      return;
    }

    const unreadIds = unreadLabNoteIds(notes);

    if (unreadIds.length === 0) {
      return;
    }

    const api = new OrderNotesApi(client);

    void api
      .markNotesAsRead(orderId, unreadIds)
      .then((updatedNotes) => {
        setNotes((previous) => mergeNotes(previous, updatedNotes));
      })
      .catch(() => {
        // Auto-mark-as-read is a best-effort convenience.
        // Failures remain silent so manual marking or a later reload can retry.
      });
  }, [client, orderId, autoMarkAsRead, notes]);

  /**
   * Keeps the message list scrolled to the latest message after loading,
   * expanding, resizing, or receiving updated notes.
   */
  useLayoutEffect(() => {
    if (isMessagesCollapsed || notesLoading) {
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      scrollMessagesToBottom('auto');
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [notes, notesLoading, isMessagesCollapsed, isMessagesLarge, scrollMessagesToBottom]);

  const handleAutoMarkAsReadChange = useCallback((value: boolean): void => {
    setAutoMarkAsRead(value);
    writeAutoMarkNotesAsRead(value);
  }, []);

  const handleMarkAsRead = useCallback(async (): Promise<void> => {
    if (!client || !orderId) {
      return;
    }

    const unreadIds = unreadLabNoteIds(notes);

    if (unreadIds.length === 0) {
      return;
    }

    try {
      setNotesErrorKey(null);

      const api = new OrderNotesApi(client);
      const updatedNotes = await api.markNotesAsRead(orderId, unreadIds);

      setNotes((previous) => mergeNotes(previous, updatedNotes));

      // Reload so the UI immediately reflects the latest server state.
      await reloadOrderNotes();
    } catch (error) {
      setNotesErrorKey(errorKeyOf(error));
    }
  }, [client, orderId, notes, reloadOrderNotes]);

  const handleSendNote = useCallback(
    async (event: FormEvent<HTMLFormElement>): Promise<void> => {
      event.preventDefault();

      if (!client || !orderId) {
        setMessageText('');
        return;
      }

      const content = messageText.trim();

      if (!content) {
        return;
      }

      setSendingNote(true);
      setSendErrorKey(null);
      setHasConflict(false);

      try {
        const api = new OrderNotesApi(client);
        const unreadIds = unreadLabNoteIds(notes);

        const result = await api.addOrderNote(orderId, content, unreadIds);

        setNotes((previous) => mergeNotes(previous, result.notes));

        window.requestAnimationFrame(() => {
          scrollMessagesToBottom('smooth');
        });

        if (result.hasConflict) {
          setHasConflict(true);
        } else {
          setMessageText('');
        }
      } catch (error) {
        setSendErrorKey(errorKeyOf(error));
      } finally {
        setSendingNote(false);
      }
    },
    [client, orderId, messageText, notes, scrollMessagesToBottom],
  );

  const toggleMessagesCollapsed = useCallback((): void => {
    setIsMessagesCollapsed((previous) => !previous);
  }, []);

  const toggleMessagesLarge = useCallback((): void => {
    setIsMessagesLarge((previous) => !previous);
  }, []);

  return {
    client,
    orderId,

    notes,
    notesLoading,
    notesErrorKey,

    messageText,
    setMessageText,

    sendingNote,
    sendErrorKey,
    hasConflict,

    autoMarkAsRead,
    handleAutoMarkAsReadChange,

    unreadLabNoteCount,
    firstUnreadLabNoteId,

    isMessagesCollapsed,
    isMessagesLarge,
    toggleMessagesCollapsed,
    toggleMessagesLarge,

    messagesScrollRef,

    reloadOrderNotes,
    handleMarkAsRead,
    handleSendNote,
  };
}

export type UseOrderNotesResult = ReturnType<typeof useOrderNotes>;
